import { useMutation, useQueries, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, stripSearchParams } from "@tanstack/react-router";
import { useId } from "react";
import { z } from "zod";

import {
	NOTIFICATION_KINDS,
	NOTIFICATION_STATES,
	SECTION_LABELS,
	type Notification,
	type WorkItem,
} from "@ravnar/api-contract";

import { ApiError } from "../api/client";
import { notificationsQueryOptions, setNotificationArchived } from "../api/notifications";
import { workItemQueryOptions } from "../api/work";
import { NotificationRowLink } from "../components/links";
import { PageHeader } from "../components/shell/app-shell";
import { Button } from "../components/ui/button";
import { buttonClassName } from "../components/ui/button-styles";
import { Card, CardHeader } from "../components/ui/card";
import { Drawer } from "../components/ui/drawer";
import { Empty } from "../components/ui/empty";
import { FilterPill, Toolbar } from "../components/ui/filter-pill";
import { Pager } from "../components/ui/pager";
import { Pill } from "../components/ui/pill";
import { Skeleton } from "../components/ui/skeleton";
import { TabPanel, Tabs } from "../components/ui/tabs";
import {
	NOTIFICATION_FILTER_LABELS,
	NOTIFICATION_FILTERS,
	NOTIFICATION_TARGET_LABELS,
	notificationTag,
	workItemCount,
	type NotificationFilter,
} from "../domain/notifications";
import { isComingSoonPath, SECTION_DOT_CLASS } from "../domain/sections";
import { workItemAsk, workItemClosing, workStatus } from "../domain/work";
import { cn } from "../lib/cn";
import { formatRelativeTime } from "../lib/format";
import { showToast } from "../lib/toast";

// `.default()` makes the param optional in links; `.catch()` turns a hand-edited bad value into the fallback.
const notificationsSearchSchema = z.object({
	tab: z.enum(NOTIFICATION_STATES).default("unattended").catch("unattended"),
	/** The kind the list is filtered to; every kind when there is none. */
	kind: z.enum(NOTIFICATION_FILTERS).optional().catch(undefined),
	/** A page link from the API, which the page never reads; the newest page when there is none. */
	page: z.string().optional().catch(undefined),
	/** The notification the side panel shows. One that is not on the page being read opens nothing. */
	notification: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/notifications")({
	validateSearch: notificationsSearchSchema,
	search: { middlewares: [stripSearchParams({ tab: "unattended" })] },
	loaderDeps: ({ search: { tab, kind, page } }) => ({ tab, kind, page }),
	// Prefetch through the Query cache so the page can use useSuspenseQuery without a loading flash.
	loader: async ({ context, deps }) => {
		try {
			await context.queryClient.query({
				...notificationsQueryOptions({ state: deps.tab, kind: deps.kind, page: deps.page }),
				staleTime: "static",
			});
		} catch (error) {
			// A page link the API no longer takes, from an old bookmark or edited by hand: start at the newest page.
			if (deps.page !== undefined && error instanceof ApiError && error.status === 400) {
				throw redirect({ to: "/notifications", search: { tab: deps.tab, kind: deps.kind }, replace: true });
			}
			throw error;
		}
	},
	head: () => ({ meta: [{ title: "Notifications · Ravnar" }] }),
	staticData: { crumbs: ["Notifications"] },
	component: NotificationsPage,
});

const tabs = [
	{ value: "unattended", label: "Unattended" },
	{ value: "archived", label: "Archived" },
] as const;

/** The dot a notification carries: its section's colour, or the workspace's grey. */
function dotClassOf(notification: Notification): string {
	return notification.section === null ? "bg-ink-4" : SECTION_DOT_CLASS[notification.section];
}

/** One work item a notification is about, as it stands now, linking to the Work page's drawer for it. */
function WorkItemLine({ item }: { readonly item: WorkItem }) {
	const { tone, label } = workStatus(item);
	return (
		<li className="flex items-start gap-2.5 border-b border-line px-3 py-2.5 last:border-0">
			<span className="min-w-0 flex-1">
				<Link to="/work" search={{ item: item.id }} className="block font-medium text-ink hover:underline">
					{item.title}
				</Link>
				<span className="block text-caption text-ink-3">
					{item.state === "open" ? workItemAsk(item) : workItemClosing(item)}
				</span>
			</span>
			<Pill tone={tone}>{label}</Pill>
		</li>
	);
}

/**
 * The work items a notification is about, each read from the API as it stands now, so one done since the notification
 * was raised says so. An item this member may not see answers 404, as one that does not exist does, and is counted
 * rather than shown: the notification names its document, never work in an area the member cannot look at.
 */
function NotificationWorkItems({ ids }: { readonly ids: readonly string[] }) {
	const headingId = useId();
	const results = useQueries({ queries: ids.map(id => workItemQueryOptions(id)) });
	const items = results.flatMap(result => (result.data === undefined ? [] : [result.data]));
	const waiting = results.filter(result => result.isPending).length;
	const hidden = results.filter(result => result.error instanceof ApiError && result.error.status === 404).length;
	const failed = results.filter(result => result.isError).length - hidden;
	return (
		<section aria-labelledby={headingId}>
			<h3 id={headingId} className="mb-2 text-ctl font-semibold text-ink">
				Work items
			</h3>
			{items.length > 0 ? (
				<ul aria-labelledby={headingId} className="rounded-card border border-line">
					{items.map(item => (
						<WorkItemLine key={item.id} item={item} />
					))}
				</ul>
			) : null}
			{waiting > 0 ? (
				<div role="status" className="mt-2 flex flex-col gap-2">
					<span className="sr-only">Loading work items…</span>
					{Array.from({ length: waiting }, (_, index) => (
						<Skeleton key={index} className="h-9 w-full" />
					))}
				</div>
			) : null}
			{hidden > 0 ? (
				<p className="mt-2 text-caption text-ink-3">{workItemCount(hidden)} you have no access to.</p>
			) : null}
			{failed > 0 ? (
				<p role="alert" className="mt-2 text-caption text-alert">
					{workItemCount(failed)} could not be loaded.
				</p>
			) : null}
		</section>
	);
}

interface NotificationDrawerProps {
	/** The notification the URL names, once it is on the page being read; the drawer is shut while there is none. */
	readonly notification: Notification | undefined;
	/** Archives it, or restores it: it leaves the page being read, which closes the drawer. */
	readonly onMove: (notification: Notification) => void;
	readonly moving: boolean;
	readonly onClose: () => void;
}

/**
 * One notification, whole, on the side: its title, when it was raised, its text, the work items it is about, and the
 * page it is about. Archiving from here moves it to the other tab, which closes the drawer.
 */
function NotificationDrawer({ notification, onMove, moving, onClose }: NotificationDrawerProps) {
	const workItemIds = notification?.workItemIds ?? [];
	return (
		<Drawer
			open={notification !== undefined}
			title={notification?.title ?? ""}
			onClose={onClose}
			footer={
				notification === undefined ? undefined : (
					<>
						<Button
							disabled={moving}
							onClick={() => {
								onMove(notification);
							}}
						>
							{notification.archived ? "Restore" : "Archive"}
						</Button>
						{/* A page still to come has nothing to open. */}
						{isComingSoonPath(notification.to) ? null : (
							<Link to={notification.to} className={buttonClassName({ variant: "primary" })}>
								Open {NOTIFICATION_TARGET_LABELS[notification.to]}
							</Link>
						)}
					</>
				)
			}
		>
			{notification === undefined ? null : (
				<div className="flex flex-col gap-4">
					<div className="flex items-center gap-2 text-caption text-ink-3">
						<span
							aria-hidden="true"
							className={cn("h-[7px] w-[7px] shrink-0 rounded-full", dotClassOf(notification))}
						/>
						<span>
							{notification.section === null ? "Workspace" : SECTION_LABELS[notification.section]}
						</span>
						<span aria-hidden="true">·</span>
						<time dateTime={notification.at} className="tabular-nums">
							{formatRelativeTime(notification.at)}
						</time>
						{notification.archived ? <Pill>Archived</Pill> : null}
					</div>
					<p className="text-body text-ink-2">{notification.description}</p>
					{workItemIds.length > 0 ? <NotificationWorkItems ids={workItemIds} /> : null}
				</div>
			)}
		</Drawer>
	);
}

/** What an empty list of one kind says, on either tab. */
const EMPTY_FILTER_TEXT: Readonly<Record<NotificationFilter, string>> = {
	work: "No notification here has work items attached.",
};

/** What the card says when the list it shows is empty: a page emptied by archiving, a kind with none, or a tab. */
function emptyText(total: number, kind: NotificationFilter | undefined, isArchived: boolean): string {
	if (total > 0) {
		return "Nothing is left on this page.";
	}
	if (kind !== undefined) {
		return EMPTY_FILTER_TEXT[kind];
	}
	return isArchived ? "Nothing archived yet." : "Nothing needs your attention.";
}

function NotificationsPage() {
	const { tab, kind, page, notification: opened } = Route.useSearch();
	const navigate = Route.useNavigate();
	const tabsId = useId();
	const queryClient = useQueryClient();
	const isArchived = tab === "archived";
	const { data: shown } = useSuspenseQuery(notificationsQueryOptions({ state: tab, kind, page }));
	const rows = shown.notifications;
	// The counts cover every notification of the tab, whichever kind is shown, so the pills keep saying how many there
	// are of each kind while one of them is chosen.
	const everyKind = NOTIFICATION_KINDS.reduce((sum, option) => sum + shown.counts[option], 0);
	const move = useMutation({
		mutationFn: setNotificationArchived,
		onSuccess: (_notification, { archived }) => {
			showToast(archived ? "Notification archived." : "Notification restored.");
		},
		// Either way the list is asked again: a notification that is gone (404) leaves it then.
		onSettled: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
	});
	const closeDrawer = () => {
		void navigate({ search: previous => ({ ...previous, notification: undefined }), replace: true });
	};

	/** Shows one kind, or every kind. A page link names the list it was made for, so the page goes. */
	function showKind(option: NotificationFilter | undefined) {
		void navigate({ search: previous => ({ ...previous, kind: option, page: undefined }), replace: true });
	}

	return (
		<div className="max-w-[880px]">
			<PageHeader
				title="Notifications"
				description="Everything the console noticed for you. Archive what you have dealt with; nothing is ever deleted."
			/>
			<div className="mb-3.5">
				<Tabs
					id={tabsId}
					label="Notifications"
					items={tabs}
					value={tab}
					onChange={value => {
						// The other tab pages on its own, so the page link of this one is left behind; the kind stays.
						void navigate({ search: { tab: value, kind }, replace: true });
					}}
				/>
			</div>
			<TabPanel id={tabsId} value={tab}>
				<Toolbar>
					<FilterPill
						active={kind === undefined}
						count={everyKind}
						onClick={() => {
							showKind(undefined);
						}}
					>
						All
					</FilterPill>
					{NOTIFICATION_FILTERS.map(option => (
						<FilterPill
							key={option}
							active={kind === option}
							count={shown.counts[option]}
							onClick={() => {
								showKind(kind === option ? undefined : option);
							}}
						>
							{NOTIFICATION_FILTER_LABELS[option]}
						</FilterPill>
					))}
				</Toolbar>
				<Card>
					<CardHeader title={`${shown.total} ${isArchived ? "archived" : "unattended"}`} />
					{rows.length > 0 ? (
						<ul aria-label={isArchived ? "Archived notifications" : "Unattended notifications"}>
							{rows.map(notification => (
								<NotificationRowLink
									key={notification.id}
									// The title opens the notification whole, on the side; the page it is about is a link there.
									from="/notifications"
									to="/notifications"
									search={previous => ({ ...previous, notification: notification.id })}
									dotClassName={dotClassOf(notification)}
									title={notification.title}
									description={notification.description}
									time={formatRelativeTime(notification.at)}
									tag={notificationTag(notification)}
									action={
										<Button
											size="sm"
											aria-label={`${isArchived ? "Restore" : "Archive"} ${notification.title}`}
											disabled={move.isPending}
											onClick={() => {
												move.mutate({ id: notification.id, archived: !isArchived });
											}}
										>
											{isArchived ? "Restore" : "Archive"}
										</Button>
									}
								/>
							))}
						</ul>
					) : (
						<Empty>{emptyText(shown.total, kind, isArchived)}</Empty>
					)}
					{shown.next === null && shown.previous === null ? null : (
						<Pager
							label="Notification pages"
							previous={
								shown.previous === null ? null : (
									<Link
										to="/notifications"
										search={{ tab, kind, page: shown.previous }}
										className={buttonClassName({ size: "sm" })}
									>
										Previous
									</Link>
								)
							}
							next={
								shown.next === null ? null : (
									<Link
										to="/notifications"
										search={{ tab, kind, page: shown.next }}
										className={buttonClassName({ size: "sm" })}
									>
										Next
									</Link>
								)
							}
						/>
					)}
				</Card>
			</TabPanel>
			<NotificationDrawer
				notification={rows.find(notification => notification.id === opened)}
				moving={move.isPending}
				onMove={notification => {
					move.mutate({ id: notification.id, archived: !notification.archived });
				}}
				onClose={closeDrawer}
			/>
		</div>
	);
}
